// The preview of a folder's file (FilePreview.tsx), the parts with no DOM: which kind of view a file
// gets, the size limits, CSV, spreadsheet column letters, Excel dates, which links a drawn file keeps, and
// asking a worker (a workbook is read in one).
// Ported from Space (C:\Github\Space\app\src\lib\preview.ts).

export type PreviewKind = 'image' | 'video' | 'audio' | 'pdf' | 'docx' | 'xlsx' | 'pptx' | 'markdown' | 'csv' | 'text' | 'zip';

const IMAGE = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'avif', 'ico'];
const VIDEO = ['mp4', 'webm', 'mov', 'm4v', 'ogv'];
const AUDIO = ['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'flac'];
const TEXT = [
  'txt', 'log', 'json', 'xml', 'yml', 'yaml', 'ini', 'cfg', 'conf', 'tsv', 'js', 'ts', 'tsx', 'jsx', 'css',
  'html', 'htm', 'xhtml', 'py', 'r', 'cs', 'java', 'sql', 'sh', 'ps1', 'bat',
];

/** "report.final.PDF" → "pdf"; '' without one. */
export const extension = (name: string): string => (/\.([^./\\]+)$/.exec(name)?.[1] ?? '').toLowerCase();

/**
 * How a file is shown in the preview; null: "No preview" and Download.
 * HTML, XML and the like are text: shown as written, never drawn. SVG is an image: only ever in <img>.
 */
export function previewKind(name: string, type: string): PreviewKind | null {
  const ext = extension(name);
  if (type.startsWith('image/') || IMAGE.includes(ext)) return 'image';
  if (type.startsWith('video/') || VIDEO.includes(ext)) return 'video';
  if (type.startsWith('audio/') || AUDIO.includes(ext)) return 'audio';
  // By its type only: the read URL shows only application/pdf in the page (isInlineSafe). A "Report.pdf" stored as
  // another type would download from the frame, and the preview stay blank.
  if (type === 'application/pdf') return 'pdf';
  if (ext === 'docx') return 'docx';
  if (ext === 'xlsx' || ext === 'xlsm') return 'xlsx';
  if (ext === 'pptx') return 'pptx';
  if (ext === 'md' || ext === 'markdown') return 'markdown';
  if (ext === 'csv' || type === 'text/csv') return 'csv';
  if (ext === 'zip' || type === 'application/zip' || type === 'application/x-zip-compressed') return 'zip';
  if (type.startsWith('text/') || TEXT.includes(ext)) return 'text';
  return null;
}

/**
 * Kinds not downloaded whole: the browser streams them, or (zip) only the file list at the end is read.
 * The rest are downloaded and drawn here, so they have a size limit.
 */
export const STREAMED: readonly PreviewKind[] = ['image', 'video', 'audio', 'pdf', 'zip'];
/** Kinds of which only the beginning (TEXT_LIMIT) is downloaded and shown, whatever their size. */
export const HEAD_ONLY: readonly PreviewKind[] = ['markdown', 'csv', 'text'];
/** The largest file downloaded whole and drawn: Word, Excel, PowerPoint. */
export const PARSE_LIMIT = 30 * 1024 * 1024;
/** Text, Markdown and CSV: only this much is shown. One byte more is downloaded: it tells a cut file. */
export const TEXT_LIMIT = 2 * 1024 * 1024;

/**
 * CSV → rows. Handles quotes; picks ; or , by which appears more in the first line.
 * A quote opens a quoted field only at its start; inside a field it is a letter (3.5" floppy, UAB "Medicina").
 */
export function parseCsv(text: string): string[][] {
  const end = text.indexOf('\n');
  const firstLine = end === -1 ? text : text.slice(0, end);
  const sep = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ';' : ',';
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && !cell) quoted = true;
    else if (ch === sep) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows;
}

/** Spreadsheet column letters: 0 → A, 25 → Z, 26 → AA. */
export function columnName(i: number): string {
  let name = '';
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) name = String.fromCharCode(65 + ((n - 1) % 26)) + name;
  return name;
}

/**
 * An Excel date as typed in the cell: "2026-10-25" or "2026-10-25 13:05:00".
 * read-excel-file puts the cell's time on UTC and rounds down (13:05 → 13:04:59.999), so: round, read in UTC.
 * No date (a phone number in a date column gives an Invalid Date), or past the year 9999: "#####", as Excel shows.
 * The number itself is lost by then.
 */
export function cellDate(v: Date): string {
  const d = new Date(Math.round(v.getTime() / 1000) * 1000);
  const iso = Number.isNaN(d.getTime()) ? '' : d.toISOString();
  if (!/^\d{4}-/.test(iso)) return '#####';
  return iso.endsWith('T00:00:00.000Z') ? iso.slice(0, 10) : iso.slice(0, 19).replace('T', ' ');
}

/**
 * What a link in a drawn file (Word, Markdown) may do:
 *   'web'     http, https, mailto: opens in a new tab
 *   'anchor'  "#name": jumps inside the file
 *   null      nothing (javascript:, data:, a path on this site …): its href goes
 */
export function linkKind(href: string): 'web' | 'anchor' | null {
  const h = href.trim();
  if (/^#./.test(h)) return 'anchor';
  return /^(https?:\/\/|mailto:)/i.test(h) ? 'web' : null;
}

/** "pathonline.net." is "pathonline.net". */
const hostOf = (url: URL) => url.hostname.replace(/\.+$/, '');

/**
 * A picture or a video in a drawn file may load from: data: and blob: (the file's own), or another site.
 * Not from this site (`origin`), nor a path on it: the browser would send the viewer's sign-in along (a /logout signs out).
 * This site is its host, whatever the scheme or port: the browser turns http://this-site/… into https.
 */
export function srcAllowed(src: string, origin: string): boolean {
  const s = src.trim();
  if (/^(data|blob):/i.test(s)) return true;
  try {
    const u = new URL(s, origin);
    return (u.protocol === 'https:' || u.protocol === 'http:') && hostOf(u) !== hostOf(new URL(origin));
  } catch {
    return false;
  }
}

/** A CSS escape: "\" and 1–6 hex digits (one space after them is part of it), or "\" and any other character. */
const ESCAPE = /\\(?:([0-9a-f]{1,6})(?:\r\n|[ \t\r\n\f])?|(\r\n|[\s\S]?))/gi;

/**
 * CSS escapes written as their characters: "\9" a tab, "\a0" a no-break space (Word's list numbers end so).
 * A quote, a backslash or a line break would change the CSS around it: a space instead.
 */
function unescapeCss(css: string): string {
  return css.replace(ESCAPE, (_, hex: string | undefined, other: string | undefined) => {
    const n = hex ? parseInt(hex, 16) : -1;
    const ch = !hex ? (other ?? '') : n === 0 || n > 0x10ffff || (n >= 0xd800 && n <= 0xdfff) ? '�' : String.fromCodePoint(n);
    return /["'\\\r\n\f]/.test(ch) ? ' ' : ch;
  });
}

/**
 * CSS as the browser reads it, with nothing that loads or leaves its box but url(). Escapes are read first (one
 * could spell "url"), then comments go (one between ":" and "host" still makes ":host"): a space each, which joins
 * nothing. Then image-set(), src() and @import stop working, and :host goes (from Word's shadow root it would style
 * the box itself).
 */
function plainCss(css: string): string {
  return unescapeCss(css)
    .replace(/\/\*[\s\S]*?(?:\*\/|$)/g, ' ')
    .replace(/@import/gi, '@x-import')
    .replace(/image-set\(/gi, 'x-image-set(')
    .replace(/src\(/gi, 'x-src(')
    .replace(/:host/gi, ':x-host');
}

/** CSS from someone's file (Word's styles), unable to load anything or to leave its box: plainCss, and url() gets an empty data: URL unless it is data: already. */
export function safeCss(css: string): string {
  return plainCss(css).replace(/url\(\s*(['"]?)(?!\s*['"]?\s*data:)/gi, 'url($1data:,');
}

/**
 * An SVG attribute that can point at something (fill="url(…)", stroke, filter, mask, clip-path, markers): as
 * safeCss, but the drawing's own parts stay (url(#id): PowerPoint's gradients). Those load nothing.
 */
export function safeSvgRef(value: string): string {
  return plainCss(value).replace(/url\(\s*(['"]?)(?!\s*['"]?\s*(?:data:|#))/gi, 'url($1data:,');
}

/** SVG attributes whose value can be url(…): safeSvgRef cleans them. */
export const SVG_REF_ATTRS = ['fill', 'stroke', 'filter', 'mask', 'clip-path', 'marker-start', 'marker-mid', 'marker-end', 'color-profile'];

/**
 * Fonts renamed in CSS (`names`: a name in lower case → its new name), in font-family lists and in Word's theme
 * variables ("--docx-majorHAnsi-font: Calibri Light"). Word's embedded fonts get names of their own so (DocxView).
 */
export function renameFonts(css: string, names: ReadonlyMap<string, string>): string {
  if (!names.size) return css;
  return css.replace(/(font-family|--docx-[\w-]+-font)(\s*:)([^;}]*)/gi, (_, prop: string, colon: string, list: string) => {
    const fonts = list.split(',').map((font) => {
      const to = names.get(font.trim().replace(/^(['"])(.*)\1$/, '$2').toLowerCase());
      return to ? ` '${to}'` : font;
    });
    return prop + colon + fonts.join(',');
  });
}

/**
 * Bytes as text: UTF-8, or else Windows-1257 (Baltic: a CSV saved by Excel in Lithuanian).
 * A half letter at the end is dropped when `cut` (the bytes stop inside the file) or when a UTF-8 letter comes
 * before it (a log copied while written). Plain ASCII then a half letter: Windows-1257 ("x;Kazlauskien" + 0xEB).
 */
function utf8OrBaltic(bytes: Uint8Array, cut: boolean): string {
  const utf8 = new TextDecoder('utf-8', { fatal: true });
  try {
    const text = utf8.decode(bytes, { stream: true });
    if (!cut && !/[\x80-￿]/.test(text)) utf8.decode(); // throws on a half letter at the end
    return text;
  } catch {
    return new TextDecoder('windows-1257').decode(bytes);
  }
}

/**
 * The first `limit` bytes as text: UTF-16 after its byte-order mark (Windows PowerShell's `>`, Notepad's
 * "Unicode"), else utf8OrBaltic. Cut inside a letter: the half letter goes.
 */
export function decodeText(data: ArrayBuffer, limit = TEXT_LIMIT): string {
  const bytes = new Uint8Array(data, 0, Math.min(limit, data.byteLength));
  const cut = data.byteLength > limit;
  const utf16 = bytes[0] === 0xff && bytes[1] === 0xfe ? 'utf-16le' : bytes[0] === 0xfe && bytes[1] === 0xff ? 'utf-16be' : null;
  // The mark itself is dropped by TextDecoder.
  return utf16 ? new TextDecoder(utf16).decode(bytes, { stream: cut }) : utf8OrBaltic(bytes, cut);
}

/**
 * A sheet's first `rows` rows and `cols` columns (what the grid shows), and its whole size: all its rows, and the
 * widest of those first rows. Only these leave the worker (sheetWorker.ts): a million rows take seconds to copy.
 */
export function firstCells<T>(data: T[][], rows: number, cols: number): { data: T[][]; size: { rows: number; cols: number } } {
  const first = data.slice(0, rows);
  return { data: first.map((r) => r.slice(0, cols)), size: { rows: data.length, cols: first.reduce((n, r) => Math.max(n, r.length), 1) } };
}

/** A worker's one answer (askWorker). */
export type WorkerAnswer<T> = { value: T } | { error: string };

/**
 * Asks a worker made for it once, then stops it: on its answer, when `signal` aborts (the viewer left the file), or
 * after `ms` (a crafted file can keep it busy for minutes). An { error } answer, or none, rejects.
 */
export function askWorker<T>(make: () => Worker, ask: unknown, signal: AbortSignal, ms: number): Promise<T> {
  if (signal.aborted) return Promise.reject(signal.reason);
  const worker = make();
  return new Promise<T>((resolve, reject) => {
    const stop = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', aborted);
      worker.terminate();
    };
    const fail = (e: unknown) => {
      stop();
      reject(e);
    };
    const aborted = () => fail(signal.reason);
    const timer = setTimeout(() => fail(new Error('The file took too long to read.')), ms);
    signal.addEventListener('abort', aborted);
    worker.onmessage = ({ data }: MessageEvent<WorkerAnswer<T>>) => {
      if ('error' in data) return fail(new Error(data.error));
      stop();
      resolve(data.value);
    };
    // It could not load, or failed where it catches nothing (out of stack in a callback).
    worker.onerror = (e) => {
      e.preventDefault();
      fail(new Error(e.message || 'The file could not be read.'));
    };
    worker.postMessage(ask);
  });
}
